import React from "react";
import PropTypes from "prop-types";
import FormattedMessage from "components/FormattedMessage";
import { Box, Flex, Text } from "ui-components";
import { getChannelFromUrl } from "utils/helpers";
import { paths } from "utils/constants";
import { COLORS } from "ui-components/theme";
import PageHeader from "components/PageHeader";
import get from "lodash/get";

const DeliveryDetailsCard = props => {
  return (
    <>
      <Title {...props} />
      <OrderDetails {...props} />
      <PersonalDetails {...props} />
      <DeliveryDetails {...props} />
      <BillingDetails {...props} />
      <ManageOrder {...props} />
    </>
  );
};

const Title = ({
  title,
  allowChange,
  allowChangePreserveUrl,
  showPersonalDetails,
  themeColors
}) => {
  const changePath = showPersonalDetails
    ? paths.PERSONAL_DETAILS
    : paths.DELIVERY_INFO;
  const changeUrl = changePath.replace(":channel", getChannelFromUrl());
  const changeButton = !allowChange
    ? {}
    : {
        url: changeUrl,
        label: "change",
        preserve: allowChangePreserveUrl || false
      };
  return !title ? null : (
    <PageHeader
      data-testid="delivery-details-card-title"
      title={title}
      paddingBottom={[0, 0]}
      changeButton={changeButton}
      themeColors={themeColors}
    />
  );
};

const OrderDetail = ({ label, value }) => {
  return !value ? null : (
    <Flex alignItems="center">
      <Box
        paddingRight={1}
        justifySelf="flex-start"
        width={"max-content"}
        whiteSpace="nowrap"
      >
        <Text py="4px" width={"max-content"}>
          <FormattedMessage id={label} />
          &nbsp;:&nbsp;
        </Text>
      </Box>
      <Box width={["max-content"]} flex={1}>
        <Text py="4px" width={["max-content"]} maxWidth={"100%"}>
          {value}
        </Text>
      </Box>
    </Flex>
  );
};

const OrderDetails = ({
  order,
  selectedNumber,
  details,
  showOrderDetails,
  subTitle
}) => {
  const orderRef = details.orderRef || order.order_ref;
  const numberSelected =
    details.selectedNumber || selectedNumber.formattedNumber;
  return !showOrderDetails ? null : (
    <Box mb="2rem">
      {subTitle ? (
        <Text mb={2} width={[1]} fontWeight="bolder">
          <FormattedMessage id={subTitle} />
        </Text>
      ) : (
        <Text mb={2} width={[1]} fontWeight="bolder">
          <FormattedMessage id="brand_type_sim_card" />
        </Text>
      )}
      <OrderDetail label={"order_id"} value={orderRef} />
      <OrderDetail label={"number_selected"} value={numberSelected} />
    </Box>
  );
};

const PersonalDetails = ({
  details,
  showPersonalDetails,
  styles,
  showName,
  showNric
}) => {
  const mb = get(styles, "personalDetails.mb");
  return !showPersonalDetails ? null : (
    <Box mb={mb || "2rem"}>
      {showName && (
        <Text mb={mb || 2} width={[1]} fontWeight="bolder">
          {details?.name}
        </Text>
      )}
      <Text mb={mb || 2} width={[1]}>
        {details?.contactNumber}
      </Text>
      <Text mb={mb || 2} width={[1]}>
        {details?.contactEmail}
      </Text>
      {showNric && details?.nric && (
        <Text mb={mb || 2} width={[1]}>
          {details.nric}
        </Text>
      )}
    </Box>
  );
};

const DeliveryDetails = ({
  details,
  showDeliveryDetails,
  showDeliveryTime
}) => {
  return !showDeliveryDetails ? null : (
    <Box mb="2rem">
      {showDeliveryTime && (
        <Text mb={2} width={[1]} fontWeight="bolder">
          {details.deliveryDateTime}
        </Text>
      )}
      <Text mb={2} width={[1]} lineHeight={1.25}>
        {details.deliveryAddress}
      </Text>
    </Box>
  );
};

const BillingDetails = ({ details, showBillingDetails }) => {
  return !showBillingDetails ? null : (
    <Box mb="2rem">
      <Text mb={2} width={[1]} lineHeight={1.25}>
        {details.billingAddress}
      </Text>
    </Box>
  );
};

const ManageOrder = ({ manageOrder, details, order }) => {
  const orderRef = get(details, "orderRef") || get(order, "order_ref");
  return !manageOrder ? null : (
    <Box mb="2rem" sx={{ a: { color: COLORS.blue[4], fontWeight: "bold" } }}>
      <FormattedMessage
        html
        id={"manager_order_link_message"}
        values={{ orderRef }}
      />
    </Box>
  );
};

DeliveryDetailsCard.defaultProps = {
  title: "",
  details: {},
  selectedNumber: {},
  order: {},
  orderSummary: {},
  allowChange: true,
  showOrderDetails: false,
  showPersonalDetails: true,
  showDeliveryDetails: true,
  showDeliveryTime: true,
  showBillingDetails: false,
  manageOrder: false,
  styles: null,
  showName: true,
  showNric: true
};

DeliveryDetailsCard.propTypes = {
  title: PropTypes.string.isRequired,
  details: PropTypes.object.isRequired
};

export default DeliveryDetailsCard;
